import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const EMRTypeDocumentationPairs = ({
  emrTypes = [],
  documentationMethods = [],
  selectedPairs = [],
  onChange,
  placeholder = "No EMR Type + Documentation Method pairs selected...",
  label = "",
  disabled = false,
  className = "",
  emptyMessage = "No options available"
}) => {
  const [pairForms, setPairForms] = useState([]);

  // Initialize with empty pair form if no existing pairs
  useEffect(() => {
    const addNewPairForm = () => {
      const newForm = {
        id: `form-${Date.now()}-${Math.random()}`,
        emr_type_id: '',
        documentation_method_id: ''
      };
      setPairForms(prevForms => [...prevForms, newForm]);
    };

    if (selectedPairs.length === 0 && pairForms.length === 0) {
      addNewPairForm();
    }
  }, [selectedPairs.length, pairForms.length]);

  // Add a new empty pair form
  const addNewPairForm = () => {
    const newForm = {
      id: `form-${Date.now()}-${Math.random()}`,
      emr_type_id: '',
      documentation_method_id: ''
    };
    setPairForms([...pairForms, newForm]);
  };

  // Update a specific pair form
  const updatePairForm = (formId, field, value) => {
    setPairForms(forms => 
      forms.map(form => {
        if (form.id === formId) {
          const updatedForm = { ...form, [field]: value };
          
          // If EMR type is selected, auto-select the related documentation method
          if (field === 'emr_type_id' && value) {
            const selectedEmrType = emrTypes.find(et => et.id === value);
            if (selectedEmrType && selectedEmrType.documentation_method_id) {
              updatedForm.documentation_method_id = selectedEmrType.documentation_method_id;
            }
          }
          
          return updatedForm;
        }
        return form;
      })
    );
  };

  // Save a pair form to the selected pairs
  const savePairForm = (formId) => {
    const form = pairForms.find(f => f.id === formId);
    if (form && form.emr_type_id && form.documentation_method_id) {
      const emrType = emrTypes.find(et => et.id === form.emr_type_id);
      const docMethod = documentationMethods.find(dm => dm.id === form.documentation_method_id);
      
      if (emrType && docMethod) {
        const newPair = {
          id: form.isEditing && form.originalPair ? form.originalPair.id : `${form.emr_type_id}-${form.documentation_method_id}-${Date.now()}`,
          emr_type_id: form.emr_type_id,
          emr_type_name: emrType.name,
          documentation_method_id: form.documentation_method_id,
          documentation_method_name: docMethod.name,
          display_text: `${emrType.name} + ${docMethod.name}`
        };
        
        if (form.isEditing && form.originalPair) {
          // Update existing pair
          const updatedPairs = selectedPairs.map(pair => 
            pair.id === form.originalPair.id ? newPair : pair
          );
          onChange(updatedPairs);
        } else {
          // Add new pair - check if it already exists
          const exists = selectedPairs.some(pair => 
            pair.emr_type_id === form.emr_type_id && pair.documentation_method_id === form.documentation_method_id
          );
          
          if (!exists) {
            onChange([...selectedPairs, newPair]);
          }
        }
        
        // Remove the form after saving
        setPairForms(forms => forms.filter(f => f.id !== formId));
      }
    }
  };

  // Remove a pair form
  const removePairForm = (formId) => {
    setPairForms(forms => forms.filter(f => f.id !== formId));
  };

  // Remove a saved pair
  const removePair = (pairId) => {
    const newPairs = selectedPairs.filter(pair => pair.id !== pairId);
    onChange(newPairs);
  };

  // Edit an existing pair
  const editExistingPair = (pair) => {
    // DON'T remove the pair from selected pairs - keep it there until we save or cancel
    // Just create a new form with the pair's values
    const newForm = {
      id: `form-${Date.now()}-${Math.random()}`,
      emr_type_id: pair.emr_type_id,
      documentation_method_id: pair.documentation_method_id,
      isEditing: true, // Mark this form as being edited
      originalPair: pair // Keep reference to original pair
    };
    setPairForms([...pairForms, newForm]);
  };

  // Check if a pair form can be saved
  const canSavePair = (formId) => {
    const form = pairForms.find(f => f.id === formId);
    return form && form.emr_type_id && form.documentation_method_id;
  };

  // Cancel editing and restore the original pair
  const cancelEdit = (formId) => {
    const form = pairForms.find(f => f.id === formId);
    if (form && form.isEditing && form.originalPair) {
      // The original pair is still in selectedPairs, so we just need to remove the form
      // No need to restore anything since we never removed it
    }
    // Remove the form
    setPairForms(forms => forms.filter(f => f.id !== formId));
  };

  // Helper function to check if a value is meaningful (not null, empty, or "None")
  const hasMeaningfulContent = (value) => {
    return value && 
           value !== 'None' && 
           value !== 'none' && 
           value !== 'null' && 
           value !== 'NULL' && 
           value.trim() !== '';
  };

  // Get available EMR types (excluding already used ones)
  const getAvailableEmrTypes = (currentFormId) => {
    const currentForm = pairForms.find(f => f.id === currentFormId);
    const usedEmrTypeIds = selectedPairs.map(pair => pair.emr_type_id);
    // Also exclude EMR types used in other forms (except the current one being edited)
    const usedInOtherForms = pairForms
      .filter(form => form.id !== currentFormId && form.emr_type_id)
      .map(form => form.emr_type_id);
    
    const allUsedIds = [...usedEmrTypeIds, ...usedInOtherForms];
    
    // Filter out used EMR types, but include the currently selected one if editing
    const availableEmrTypes = emrTypes.filter(emrType => 
      !allUsedIds.includes(emrType.id) || 
      (currentForm && currentForm.emr_type_id === emrType.id)
    );
    
    return availableEmrTypes;
  };

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {label}
        </label>
      )}
      
      {/* Display saved pairs - Show full names in edit mode */}
      {selectedPairs.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {selectedPairs
              .filter(pair => {
                // Hide pairs that are currently being edited
                const isBeingEdited = pairForms.some(form => 
                  form.isEditing && form.originalPair && form.originalPair.id === pair.id
                );
                return !isBeingEdited;
              })
              .map(pair => (
              <span
                key={pair.id}
                className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200"
              >
                <span className="font-semibold">
                  {pair.display_text || `${pair.emr_type_name} + ${pair.documentation_method_name}`}
                </span>
                {!disabled && (
                  <>
                    <button
                      type="button"
                      className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-blue-200"
                      onClick={() => editExistingPair(pair)}
                      title="Edit pair"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-red-200"
                      onClick={() => removePair(pair.id)}
                      title="Remove pair"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Pair forms */}
      {!disabled && (
        <div className="space-y-4">
          {pairForms.map((form, index) => (
            <div key={form.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
              <div className="flex items-center justify-between mb-3">
                {form.isEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={() => cancelEdit(form.id)}
                      className="text-gray-500 hover:text-gray-700 flex items-center gap-1"
                      title="Cancel editing"
                    >
                      <ArrowLeftIcon className="w-4 h-4" />
                      <span className="text-xs">Back</span>
                    </button>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-blue-600 font-medium">Editing existing pair</span>
                      <button
                        type="button"
                        onClick={() => {
                          if (form.isEditing && form.originalPair) {
                            // If editing, remove the original pair from selectedPairs
                            const newPairs = selectedPairs.filter(pair => pair.id !== form.originalPair.id);
                            onChange(newPairs);
                          }
                          // Remove the form
                          setPairForms(forms => forms.filter(f => f.id !== form.id));
                        }}
                        className="text-red-500 hover:text-red-700"
                        title="Remove pair"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setPairForms(forms => forms.filter(f => f.id !== form.id));
                    }}
                    className="text-red-500 hover:text-red-700 ml-auto"
                    title="Remove pair"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              <div className="space-y-3">
                {/* EMR Type selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    EMR Types:
                  </label>
                  <select
                    value={form.emr_type_id}
                    onChange={(e) => updatePairForm(form.id, 'emr_type_id', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select EMR Type...</option>
                    {getAvailableEmrTypes(form.id).map(emrType => (
                      <option key={emrType.id} value={emrType.id}>
                        {emrType.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Documentation Method selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Documentation Methods:
                  </label>
                  <select
                    value={form.documentation_method_id}
                    onChange={(e) => updatePairForm(form.id, 'documentation_method_id', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Documentation Method...</option>
                    {documentationMethods.map(method => (
                      <option key={method.id} value={method.id}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                  {/* Show auto-selected indicator */}
                  {form.emr_type_id && form.documentation_method_id && (
                    <div className="mt-1 text-xs text-green-600">
                      ✓ Auto-selected based on EMR Type
                    </div>
                  )}
                </div>

                {/* Save button */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => savePairForm(form.id)}
                    disabled={!canSavePair(form.id)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg ${
                      canSavePair(form.id)
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Save Pair
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Add new pair button */}
          <button
            type="button"
            onClick={addNewPairForm}
            className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add New Pair
          </button>
        </div>
      )}

      {/* Show placeholder when no pairs and no forms */}
      {selectedPairs.length === 0 && pairForms.length === 0 && (
        <div className="text-gray-500 text-sm italic">
          {placeholder}
        </div>
      )}
    </div>
  );
};

export default EMRTypeDocumentationPairs;